<template>
  <FullScreenOverlay :initializing="transitioning">
    <div class="fetching-container" v-if="transitioning">
      <p class="lead text-muted no-margin-bottom">
        <i class="fa fa-circle-o-notch fa-spin"/> Preparing...
      </p>
    </div>

    <PrintLayoutContainer v-if="!transitioning">
      <div
        class="container top-bar"
        slot="top"
      >
        <div class="print-quota" v-if="isTrial">
          <p class="text-muted">
            {{ remainingPrints }} of {{ printQuota }} prints remaining this month
          </p>
        </div>
        <div class="print-actions">
          <p><button
            class="btn btn-block btn-primary"
            @click="printAndClose"><i class="fa fa-print"/> Print</button></p>
          <p><button class="btn btn-block btn-default" @click="closePreview"><i class="fa fa-times"/> Cancel</button></p>
        </div>
      </div>
      <div slot="body">
        <PrintPage>
          <p class="no-margin-bottom subheader-text"><small>Printed by <b>{{ user.name }} ({{ user.email }})</b> from folder <b class="fs-block">{{ studentName }}</b> on {{ currentTime }}</small></p>
          <p class="text-muted subheader-text" v-if="isTrial"><small>Per our legal Terms of Use, the print feature is intended to facilitate communication and collaboration, and not to be used for illicit saving or distribution of proprietary content. <b>{{ remainingPrints }} of {{ printQuota }} prints remaining</b> for this month.</small></p>
          <h1>{{ title }}</h1>
          <div class="goal-text">
            <div class="goal-text--header">
              <strong>Grade Level</strong>
            </div>
            <div class="goal-text--body">
              <WikiText :text="gradeLevelGoal" />
            </div>
          </div>
          <div
            class="goal-text"
            v-for="goal in adaptedGoals"
            :key="goal.level">
            <div class="goal-text--header">
              <strong>{{ goal.level }} {{ adaptedGoalLabel }}</strong>
            </div>
            <div class="goal-text--body">
              <WikiText :text="goal.text" />
            </div>
          </div>
          <h2 v-if="groupedStandards.length > 0">Standards</h2>
          <div v-for="(standard, index) in standardsAsArray" :key="index">
            <div class="print-panel">
              <strong>{{ panelHeaderTextForStandard(standard) }}</strong>
              <StandardView
                :set-code="standard.standard_set_code"
                :long-ref="standard.standard_long_ref"
                :short-ref="standard.standard_short_ref"
                :core-purpose="standard.core_purpose"
                :info-obj="standard"
                :standard-text="standard.standard_text" />
            </div>
          </div>
        </PrintPage>
      </div>
    </PrintLayoutContainer>
  </FullScreenOverlay>
</template>

<script>
import FullScreenOverlay from '@/components/FullScreenOverlay'
import PrintLayoutContainer from '@/components/PrintLayoutContainer'
import WikiText from '@/components/WikiText.vue'
import PrintPage from '@/components/PrintPage'
import StandardView from '@/standards/StandardView.vue'
import { isSubjectLifeSkills, isSubjectELL } from '@/util/goals'
import { mapGetters, mapState } from 'vuex'
import { panelHeaderTextForStandard } from '@/components/goals/common/standard_panel_header_text.js'

export default {
  name: 'PrintableSavedGoalPage',
  components: {
    FullScreenOverlay,
    PrintLayoutContainer,
    WikiText,
    PrintPage,
    StandardView
  },
  props: {
    title: {
      type: String,
      required: true
    },
    adaptedGoals: {
      type: Array,
      required: true
    },
    gradeLevelGoal: {
      type: String,
      required: true
    },
    studentName: {
      type: String,
      required: true
    },
    groupedStandards: {
      type: Array,
      default() {
        return []
      }
    },
    subject: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      transitioning: true
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('contentAccess', ['printQuota', 'printCount']),
    ...mapGetters('user', ['isTrial']),
    adaptedGoalLabel() {
      if (isSubjectELL(this.subject)) {
        return 'Level'
      } else if (isSubjectLifeSkills(this.subject)) {
        return 'Support'
      } else {
        return 'Level of Support'
      }
    },
    currentTime() {
      return new Date()
        .toISOString()
        .split('T')
        .join(' ')
    },
    remainingPrints() {
      var remaining = this.printQuota - this.printCount
      return remaining > -1 ? remaining : 0
    },
    standardsAsArray() {
      var standards = []
      this.groupedStandards.forEach(set => {
        standards = standards.concat(set.standards)
      })
      return standards
    }
  },
  methods: {
    closePreview() {
      this.$emit('update:show', false)
    },
    printAndClose() {
      window.print()
      this.$emit('print')
      this.closePreview()
    },
    panelHeaderTextForStandard(standard) {
      return panelHeaderTextForStandard(standard)
    }
  },
  created() {
    this.transitioning = true

    // set a minimum "wait time" so the user perceives the transition of the printable overlay animated
    // without this, the overlay could appear very quickly and user might think they navigated to a new page
    setTimeout(() => {
      this.transitioning = false
    }, 500)
  }
}
</script>

<style scoped lang="less">
.rounded-border {
  border: solid #d3d3d3 1px;
  border-radius: 2px;
}
h2 {
  font-size: 18px;
  font-weight: 700;
}
.print-panel {
  .rounded-border;
  padding: 15px;
}
.fetching-container {
  text-align: center;
  padding: 32px;
}
.personalized-for {
  font-weight: 400;
  margin-top: 10px;
  color: #666666;
  font-size: 18px;
}
.subheader-text {
  line-height: 13px;
}
.top-bar {
  display: flex;
  max-width: 100%;
  .print-quota {
    flex: 2 1 auto;
    padding-top: 6px;
    text-align: right;
  }
  .print-actions {
    padding-left: 12px;
    flex: 0 1 auto;
    margin-left: auto;
  }
}

.goal-text {
  .print-panel;
  border: 1px solid rgb(229, 229, 229);
  padding: 15px;
  margin-bottom: 5px;
  .goal-text--header {
    border-bottom: 1px solid rgb(229, 229, 229);
    margin-bottom: 5px;
  }
}

@media print {
  blockquote {
    border: 0;
  }
}
</style>
